import { StringDevUIFieldDescriptor } from "./StringDevUISchema";
import { StringDevUIField } from "./fields/StringDevUIField";
import { ensureStringDevtoolsFieldStyles } from "./StringDevUIFields.css";
import { StringDevFieldNumber } from "./fields/StringDevFieldNumber";
import { StringDevFieldRange } from "./fields/StringDevFieldRange";
import { StringDevFieldColor } from "./fields/StringDevFieldColor";
import { StringDevFieldSelect } from "./fields/StringDevFieldSelect";
import { StringDevFieldToggle } from "./fields/StringDevFieldToggle";
import { StringDevFieldDivider } from "./fields/StringDevFieldDivider";

/**
 * Constructs a settings panel from a StringDevUIFieldDescriptor schema.
 *
 * The builder is module-agnostic — it reads the schema,
 * instantiates the correct field components, and wires up
 * change callbacks. The module never touches the UI directly.
 */
export class StringDevUIBuilder {
  private fields: StringDevUIField[] = [];
  private container: HTMLDivElement | null = null;

  /**
   * Builds a DOM element containing all fields described by the schema.
   *
   * @param schema    Array of field descriptors from a devtool module
   * @param values    Current values (merged with defaults)
   * @param onChange  Callback fired when any field value changes
   * @returns         The container DOM element
   */
  build(
    schema: StringDevUIFieldDescriptor[],
    values: Record<string, any>,
    onChange: (key: string, value: any) => void,
  ): HTMLDivElement {
    ensureStringDevtoolsFieldStyles();
    this.destroy();

    const container = document.createElement("div");
    container.setAttribute("data-string-dev-settings-container", "");

    for (const descriptor of schema) {
      const field = this.createField(descriptor, values, onChange);
      if (field) {
        this.fields.push(field);
        container.appendChild(field.build());
      }
    }

    this.container = container;
    return container;
  }

  /**
   * Updates all field values from an external state object.
   */
  updateValues(values: Record<string, any>): void {
    for (const field of this.fields) {
      const key = (field as any).key;
      if (key && key in values) {
        field.setValue(values[key]);
      }
    }
  }

  /**
   * Tears down all fields and removes the container.
   */
  destroy(): void {
    for (const field of this.fields) {
      field.destroy();
    }
    this.fields = [];
    this.container?.remove();
    this.container = null;
  }

  private createField(
    descriptor: StringDevUIFieldDescriptor,
    values: Record<string, any>,
    onChange: (key: string, value: any) => void,
  ): StringDevUIField | null {
    switch (descriptor.type) {
      case "number":
        return new StringDevFieldNumber(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.min,
          descriptor.max,
          descriptor.step,
        );

      case "range":
        return new StringDevFieldRange(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.min,
          descriptor.max,
          descriptor.step,
          descriptor.units,
          values[descriptor.key + "Unit"] ?? descriptor.defaultUnit,
        );

      case "color":
        return new StringDevFieldColor(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
        );

      case "select":
        return new StringDevFieldSelect(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.options,
        );

      case "toggle":
        return new StringDevFieldToggle(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
        );

      case "divider":
        return new StringDevFieldDivider(descriptor.label);

      default:
        return null;
    }
  }
}
